/**
 * EnquiryDetail.jsx — Tendering team's working surface for an enquiry.
 *
 * What's here:
 * 1. Document repository (BOQ · Tender Doc · GSS · SCC · Drawing · Other)
 * 2. AI Extract — reads uploaded docs and fills BLANK fields only (never
 *    overwrites BD's manual entries)
 * 3. Rich tender attributes sectioned per the screenshots:
 *      Basic Info · Financial Terms · Contract Clauses
 * 4. Push-to-Planning AI button (active when stage === "Awarded")
 *
 * Backend wired:
 *   GET    /enquiries/{id}
 *   PUT    /enquiries/{id}
 *   POST   /enquiries/{id}/documents/upload   (multipart, ?kind=)
 *   GET    /enquiries/{id}/documents
 *   DELETE /enquiries/{id}/documents/{doc_id}
 *   POST   /enquiries/{id}/ai-extract
 *   POST   /enquiries/{id}/push-to-planning
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Sparkles, Send,
  Building2, IndianRupee, FileCheck2, Loader2, ShieldAlert,
  AlertTriangle,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut, apiPost, api } from '../../lib/api';
import { formatDateTime as _fmtDT } from '../../lib/format';
import QualificationSWOT from './QualificationSWOT';
import { CategoryMultiCombobox, NatureCombobox, LocationCombobox } from '../../components/pickers/ProjectClientPickers';
import DocumentUploader, { ENQUIRY_DOC_CATEGORIES } from '../../components/DocumentUploader';
import EnquiryDeadlinesPanel from '../../components/crm/EnquiryDeadlinesPanel';
import EnquiryRevisionsTab from '../../components/crm/EnquiryRevisionsTab';
import EnquiryActivityTab from '../../components/crm/EnquiryActivityTab';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const inp = 'w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-[#3b4872] bg-white';
const lbl = 'text-[11px] uppercase tracking-widest text-slate-500 font-display font-bold';
const inpInvalid = 'border-rose-300 ring-1 ring-rose-200';

function Field({ label, value, onChange, type = 'text', placeholder = '', testid, required = false, invalid = false }) {
  return (
    <div>
      <label className={lbl}>{label}{required && <span className="text-rose-600 ml-0.5">*</span>}</label>
      <input
        type={type}
        value={value ?? ''}
        placeholder={placeholder || '—'}
        onChange={(e) => onChange(e.target.value)}
        className={`${inp} mt-1 ${invalid ? inpInvalid : ''}`}
        data-testid={testid}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testid, required = false, invalid = false, disabled = false, hint }) {
  return (
    <div>
      <label className={lbl}>{label}{required && <span className="text-rose-600 ml-0.5">*</span>}</label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`${inp} mt-1 ${invalid ? inpInvalid : ''} ${disabled ? 'bg-slate-100 text-slate-600 cursor-not-allowed' : ''}`}
        data-testid={testid}
      >
        <option value="">— Select —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {hint && <p className="text-[10px] text-slate-400 mt-1" data-testid={testid ? `${testid}-hint` : undefined}>{hint}</p>}
    </div>
  );
}

function Textarea({ label, value, onChange, rows = 3, testid }) {
  return (
    <div className="sm:col-span-2 lg:col-span-3">
      <label className={lbl}>{label}</label>
      <textarea rows={rows} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={`${inp} mt-1`} data-testid={testid} />
    </div>
  );
}

function Section({ title, hint, icon, children, collapsible = false, defaultOpen = true, count = null, testid, badge = null }) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = collapsible ? open : true;
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 space-y-3" data-testid={testid}>
      <header
        className={`flex items-start gap-3 pb-3 border-b border-slate-100 ${collapsible ? 'cursor-pointer select-none' : ''}`}
        onClick={collapsible ? () => setOpen((o) => !o) : undefined}
        data-testid={testid ? `${testid}-header` : undefined}
      >
        <div className="p-2 rounded-md bg-[#3b4872]/10 text-[#3b4872]">{icon}</div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold tracking-wide uppercase text-sm text-slate-800 flex items-center gap-2 flex-wrap">
            {title}
            {count != null && (
              <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{count} field{count === 1 ? '' : 's'}</span>
            )}
            {badge}
          </h3>
          {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
        </div>
        {collapsible && (
          <span className="text-slate-400 text-xs font-display font-bold uppercase tracking-wide shrink-0">
            {isOpen ? '−' : '+'}
          </span>
        )}
      </header>
      {isOpen && children}
    </section>
  );
}

const BLANK_ENQUIRY = {
  client: '', project_name: '', stage: 'Lead Identified', source: 'BD Direct',
  categories: [],
  source_links: [], custom_fields: {},
};

// Field groups used to flag whole sections as "AI-filled" when the extraction
// populated any field within them.
const FINANCIAL_FIELDS = ['tender_fee', 'emd_type', 'emd_amount', 'defect_liability_period',
  'mobilisation_advance_pct', 'retention_pct', 'ra_billing_cycle', 'client_payment_risk'];
const CONTRACT_FIELDS = ['basic_material_rate', 'price_escalation_clause', 'ld_clause', 'bonus_clause'];

const AiFilledBadge = ({ section }) => (
  <span
    className="text-[9px] font-display font-bold uppercase tracking-wide bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1 normal-case"
    data-testid={`enq-ai-filled-badge-${section}`}
  >
    <Sparkles size={9} /> AI-filled
  </span>
);

const STAGE_ORDER = [
  'Lead Identified', 'Provisional Qualification', 'Site Visit',
  'Costing and Documentation', 'Proposal Submitted', 'Negotiation',
  'Won', 'Lost', 'On Hold',
];

const STAGE_SECTIONS = {
  'Lead Identified':           ['basic_info', 'tender_docs'],
  'Provisional Qualification': ['basic_info', 'tender_docs', 'swot'],
  'Site Visit':                ['basic_info', 'tender_docs', 'site_visit'],
  'Costing and Documentation': ['basic_info', 'tender_docs', 'financial_terms', 'contract_clauses'],
  'Proposal Submitted':        ['basic_info', 'tender_docs', 'financial_terms', 'contract_clauses'],
  'Negotiation':               ['basic_info', 'financial_terms', 'contract_clauses'],
  'Won':                       ['basic_info'],
  'Lost':                      ['basic_info'],
  'On Hold':                   ['basic_info'],
  // Legacy compatibility
  'New Enquiry':   ['basic_info', 'tender_docs'],
  'Qualification': ['basic_info', 'tender_docs', 'swot'],
  'Costing':       ['basic_info', 'financial_terms', 'contract_clauses'],
  'Proposal':      ['basic_info', 'financial_terms', 'contract_clauses'],
  'Awarded':       ['basic_info'],
};

const STAGE_COLORS = {
  'Lead Identified': 'bg-cyan-50 text-cyan-800 border-cyan-200',
  'Provisional Qualification': 'bg-blue-50 text-blue-800 border-blue-200',
  'Site Visit': 'bg-amber-50 text-amber-800 border-amber-200',
  'Costing and Documentation': 'bg-indigo-50 text-indigo-800 border-indigo-200',
  'Proposal Submitted': 'bg-violet-50 text-violet-800 border-violet-200',
  'Negotiation': 'bg-orange-50 text-orange-800 border-orange-200',
  'Won': 'bg-emerald-50 text-emerald-800 border-emerald-200',
  'Lost': 'bg-rose-50 text-rose-800 border-rose-200',
  'On Hold': 'bg-slate-100 text-slate-700 border-slate-300',
};

function MeetingsTab({ enqId }) {
  const MTYPES = [['pre_bid', 'Pre-bid'], ['clarification', 'Clarification'], ['negotiation', 'Negotiation'], ['site', 'Site']];
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mom, setMom] = useState(null);
  const [f, setF] = useState({ date: '', meeting_type: 'pre_bid', attendees_ours: '', attendees_theirs: '', notes: '', fu_text: '', fu_owner: '', fu_due: '' });
  const load = async () => { try { const r = await apiGet(`/enquiries/${enqId}/meetings`); setItems(r.items || []); } catch { /* */ } };
  useEffect(() => { if (enqId) load(); }, [enqId]);
  const uploadMom = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post(`/enquiries/${enqId}/meetings/upload-mom`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMom(r.data); toast.success('MOM attached');
    } catch { toast.error('MOM upload failed'); }
  };
  const submit = async () => {
    if (!f.date) { toast.error('Meeting date required'); return; }
    setSaving(true);
    try {
      const follow_ups = f.fu_text.trim() ? [{ text: f.fu_text, owner: f.fu_owner, due_date: f.fu_due || null, done: false }] : [];
      await apiPost(`/enquiries/${enqId}/meetings`, {
        date: f.date, meeting_type: f.meeting_type,
        attendees_ours: f.attendees_ours ? f.attendees_ours.split(',').map((x) => x.trim()).filter(Boolean) : [],
        attendees_theirs: f.attendees_theirs, notes: f.notes, mom_doc: mom, follow_ups,
      });
      toast.success('Meeting logged' + (follow_ups.length ? ' · follow-up added to My Tasks' : ''));
      setShow(false); setMom(null);
      setF({ date: '', meeting_type: 'pre_bid', attendees_ours: '', attendees_theirs: '', notes: '', fu_text: '', fu_owner: '', fu_due: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save meeting'); }
    finally { setSaving(false); }
  };
  const I = 'mt-1 w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm';
  const L = 'block text-[10px] font-display font-bold uppercase tracking-wider text-slate-500';
  return (
    <div className="space-y-3" data-testid="enq-meetings-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold uppercase tracking-wide text-sm">Meetings</h3>
        <button onClick={() => setShow(true)} className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-bold uppercase tracking-wider" data-testid="meeting-add-btn">+ Log meeting</button>
      </div>
      {items.length === 0 ? <p className="text-xs text-slate-400" data-testid="meetings-empty">No meetings logged yet.</p> : (
        <div className="space-y-2">
          {items.map((m) => (
            <div key={m.id} className="border border-slate-200 rounded-md p-3" data-testid={`meeting-${m.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{(MTYPES.find((t) => t[0] === m.meeting_type) || [])[1] || m.meeting_type} · {(m.date || '').slice(0, 10)}</span>
                {m.mom_doc?.storage_path && <a href={`${(api.defaults.baseURL || '')}/enquiries/${enqId}/meetings/${m.id}/mom`} target="_blank" rel="noreferrer" className="text-[10px] text-[#3b4872] underline" data-testid={`meeting-mom-${m.id}`}>MOM</a>}
              </div>
              {m.attendees_ours?.length ? <p className="text-[11px] text-slate-500 mt-0.5">Ours: {m.attendees_ours.join(', ')}</p> : null}
              {m.attendees_theirs ? <p className="text-[11px] text-slate-500">Theirs: {m.attendees_theirs}</p> : null}
              {m.notes ? <p className="text-xs mt-1">{m.notes}</p> : null}
              {(m.follow_ups || []).map((fu, i) => <p key={i} className="text-[11px] text-amber-700 mt-1">↳ Follow-up: {fu.text}{fu.owner ? ` · ${fu.owner}` : ''}{fu.due_date ? ` · due ${fu.due_date}` : ''}</p>)}
            </div>
          ))}
        </div>
      )}
      {show && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-md w-full max-w-lg my-6 p-5 space-y-3" data-testid="meeting-form">
            <h3 className="font-display font-bold text-sm">Log meeting</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={L}>Date</label><input type="date" className={I} value={f.date} onChange={(e) => setF((s) => ({ ...s, date: e.target.value }))} data-testid="meeting-date" /></div>
              <div><label className={L}>Type</label><select className={I} value={f.meeting_type} onChange={(e) => setF((s) => ({ ...s, meeting_type: e.target.value }))} data-testid="meeting-type">{MTYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            </div>
            <div><label className={L}>Attendees — ours (comma separated)</label><input className={I} value={f.attendees_ours} onChange={(e) => setF((s) => ({ ...s, attendees_ours: e.target.value }))} data-testid="meeting-attendees-ours" /></div>
            <div><label className={L}>Attendees — theirs</label><input className={I} value={f.attendees_theirs} onChange={(e) => setF((s) => ({ ...s, attendees_theirs: e.target.value }))} data-testid="meeting-attendees-theirs" /></div>
            <div><label className={L}>Notes</label><textarea rows={2} className={I} value={f.notes} onChange={(e) => setF((s) => ({ ...s, notes: e.target.value }))} data-testid="meeting-notes" /></div>
            <div><label className={L}>MOM file</label><input type="file" className="mt-1 text-xs" onChange={(e) => uploadMom(e.target.files?.[0])} data-testid="meeting-mom-upload" />{mom && <span className="text-[10px] text-emerald-700 ml-2">{mom.label}</span>}</div>
            <div className="border-t pt-2">
              <label className={L}>Follow-up (goes to My Tasks)</label>
              <input className={I} placeholder="Follow-up task" value={f.fu_text} onChange={(e) => setF((s) => ({ ...s, fu_text: e.target.value }))} data-testid="meeting-fu-text" />
              <div className="grid grid-cols-2 gap-3 mt-2">
                <input className={I} placeholder="Owner (name)" value={f.fu_owner} onChange={(e) => setF((s) => ({ ...s, fu_owner: e.target.value }))} data-testid="meeting-fu-owner" />
                <input type="date" className={I} value={f.fu_due} onChange={(e) => setF((s) => ({ ...s, fu_due: e.target.value }))} data-testid="meeting-fu-due" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => { setShow(false); setMom(null); }} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
              <button onClick={submit} disabled={saving} className="px-4 py-1.5 bg-[#3b4872] text-white text-xs font-bold rounded-md disabled:opacity-50" data-testid="meeting-submit">{saving ? 'Saving…' : 'Save meeting'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function CostingVendorsPanel({ enqId }) {
  const [vendors, setVendors] = useState([]);
  const [adding, setAdding] = useState({ name: '', category: '', item: '', rate: '' });
  const [rateDraft, setRateDraft] = useState({});
  const load = async () => {
    try {
      const r = await apiGet('/potential-vendors', { source_enquiry_id: enqId });
      setVendors(r.items || r.vendors || (Array.isArray(r) ? r : []));
    } catch { /* ignore */ }
  };
  useEffect(() => { if (enqId) load(); }, [enqId]);
  const addVendor = async () => {
    if (!adding.name.trim()) { toast.error('Vendor name required'); return; }
    try {
      const rates = adding.rate ? [{ item: (adding.item || '').trim(), rate: Number(adding.rate), noted_at: new Date().toISOString() }] : [];
      await apiPost('/potential-vendors', { name: adding.name, category: adding.category, source_enquiry_id: enqId, quoted_rates: rates });
      toast.success('Vendor added to costing');
      setAdding({ name: '', category: '', item: '', rate: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add vendor'); }
  };
  // Tendering_30Sep2026 §7: an in-page item/rate field replaces the old
  // window.prompt() flow — item stays optional, only a positive rate is required.
  const addRate = async (v) => {
    const draft = rateDraft[v.id] || {};
    const rate = Number(draft.rate);
    if (!rate || rate <= 0) { toast.error('Enter a rate'); return; }
    try {
      const rates = [...(v.quoted_rates || []), { item: (draft.item || '').trim(), rate, noted_at: new Date().toISOString() }];
      await apiPut(`/potential-vendors/${v.id}`, { name: v.name, category: v.category, source_enquiry_id: enqId, quoted_rates: rates });
      toast.success('Rate added');
      setRateDraft((s) => ({ ...s, [v.id]: { item: '', rate: '' } }));
      load();
    } catch { toast.error('Could not add rate'); }
  };
  const I = 'border border-slate-300 rounded-md px-2 py-1 text-sm';
  return (
    <div className="border border-slate-200 rounded-md p-4 bg-white mb-4" data-testid="costing-vendors-panel">
      <div className="text-sm font-display font-bold mb-1">Vendors &amp; rates (Costing)</div>
      <p className="text-[11px] text-slate-500 mb-3">Potential vendors gathered at the site visit and added here — their quoted rates feed costing. Rates are editable in place.</p>
      {vendors.length === 0 ? (
        <p className="text-xs text-slate-400 mb-3" data-testid="costing-vendors-empty">No vendors yet for this enquiry.</p>
      ) : (
        <div className="space-y-2 mb-3">
          {vendors.map((v) => (
            <div key={v.id} className="border border-slate-100 rounded-sm p-2" data-testid={`costing-vendor-${v.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">{v.name} {v.category ? <span className="text-[10px] text-slate-400">· {v.category}</span> : null}</div>
              </div>
              <div className="flex flex-wrap gap-2 mt-1">
                {(v.quoted_rates || []).length === 0 ? <span className="text-[10px] text-slate-400">no rates</span> :
                  (v.quoted_rates || []).map((q, i) => (
                    <span key={i} className="text-[10px] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">{q.item || '(no item)'}: ₹{Number(q.rate).toLocaleString('en-IN')}{q.uom ? `/${q.uom}` : ''}</span>
                  ))}
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <input
                  className="border border-slate-300 rounded px-1.5 py-0.5 text-[11px] w-28"
                  placeholder="Item (optional)"
                  value={rateDraft[v.id]?.item || ''}
                  onChange={(e) => setRateDraft((s) => ({ ...s, [v.id]: { ...(s[v.id] || {}), item: e.target.value } }))}
                  data-testid={`costing-rate-item-${v.id}`}
                />
                <input
                  className="border border-slate-300 rounded px-1.5 py-0.5 text-[11px] w-20"
                  placeholder="Rate ₹"
                  type="number"
                  value={rateDraft[v.id]?.rate || ''}
                  onChange={(e) => setRateDraft((s) => ({ ...s, [v.id]: { ...(s[v.id] || {}), rate: e.target.value } }))}
                  data-testid={`costing-rate-value-${v.id}`}
                />
                <button onClick={() => addRate(v)} className="text-[10px] text-[#3b4872] font-semibold underline" data-testid={`costing-add-rate-${v.id}`}>+ Add</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-end">
        <input className={I} placeholder="Vendor name" value={adding.name} onChange={(e) => setAdding((s) => ({ ...s, name: e.target.value }))} data-testid="costing-vendor-name" />
        <input className={I} placeholder="Category" value={adding.category} onChange={(e) => setAdding((s) => ({ ...s, category: e.target.value }))} data-testid="costing-vendor-category" />
        <input className={I} placeholder="Item (optional)" value={adding.item} onChange={(e) => setAdding((s) => ({ ...s, item: e.target.value }))} data-testid="costing-vendor-item" />
        <input className={I} placeholder="Rate" type="number" value={adding.rate} onChange={(e) => setAdding((s) => ({ ...s, rate: e.target.value }))} data-testid="costing-vendor-rate" />
        <button onClick={addVendor} className="px-3 py-1.5 bg-[#3b4872] text-white text-xs font-bold rounded-md" data-testid="costing-vendor-add">Add vendor</button>
      </div>
    </div>
  );
}


export default function EnquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // /crm/enquiries/new  → creation mode
  // /crm/enquiries/:id  → edit mode
  const isCreating = !id || id === 'new';
  const [enq, setEnq] = useState(isCreating ? { ...BLANK_ENQUIRY } : null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(!isCreating);
  const [saving, setSaving] = useState(false);
  const [saveBlockMessage, setSaveBlockMessage] = useState(null); // inline block message
  const [extracting, setExtracting] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [tab, setTab] = useUrlTab('tab', 'details');
  const [activeKind, setActiveKind] = useState('BOQ');
  // Fields the AI populated in this session (merged with persisted
  // enq.ai_filled_fields) to drive the per-section "AI-filled" badge.
  const [aiAppliedKeys, setAiAppliedKeys] = useState([]);
  // GO/NO-GO management approval state (must be here before early returns)
  const [approvalNote, setApprovalNote] = useState('');
  const [approvalOverride, setApprovalOverride] = useState(false);
  const [finalNote, setFinalNote] = useState('');

  // Iter 137 — atomic refs to kill the duplicate-draft race.
  //
  //   savedIdRef           — confirmed enquiry id, set only after a successful
  //                          POST /enquiries (or initial load in edit mode).
  //                          Survives the `id`-from-useParams lag between the
  //                          replace-navigate and the next render.
  //   ensureSavedPromiseRef — holds the in-flight create POST as a single
  //                           Promise; concurrent callers (auto-save timer +
  //                           uploadDocs + runAiExtract) await this SAME promise
  //                           instead of starting a second POST.
  //   autoSaveTriedRef      — debounce-effect "I have queued a save" gate, set
  //                           BEFORE the await so a re-render with stale state
  //                           cannot queue a second save.
  //   justSavedRef          — skips the post-navigate load() once, so the GET
  //                           that fires after the URL change does not overwrite
  //                           local edits the user typed between client+project
  //                           and the auto-save completing.
  const savedIdRef = useRef(isCreating ? null : id);
  const ensureSavedPromiseRef = useRef(null);
  const autoSaveTriedRef = useRef(false);
  const justSavedRef = useRef(false);

  // Keep savedIdRef synced with URL changes from elsewhere (browser back/forward,
  // deep link, manual save POST).
  useEffect(() => {
    if (id && id !== 'new') savedIdRef.current = id;
  }, [id]);

  // Iter 165 — single source of truth for the docs list is now
  // `GET /api/documents/enquiry/{id}` (the unified lifecycle store).
  // The legacy embedded `enquiry.tender_documents[]` array still gets
  // dual-written on upload/delete for backend consumers (AI extract,
  // push-to-planning), but the UI reads from db.documents exclusively
  // so opportunity-uploaded docs that were lineage-linked at push time
  // surface here automatically.
  const fetchEnquiryDocs = async (enqId) => {
    try {
      const r = await apiGet(`/documents/enquiry/${enqId}`);
      return r.documents || [];
    } catch {
      return [];
    }
  };

  const load = async () => {
    if (isCreating) { setLoading(false); return; }
    // If ensureSaved just navigated us here, enq + docs are already in state and
    // freshly seeded by the POST response — skip the GET to preserve any local
    // edits the user made between auto-save firing and now.
    if (justSavedRef.current) { justSavedRef.current = false; setLoading(false); return; }
    setLoading(true);
    try {
      const e = await apiGet(`/enquiries/${id}`);
      // Backend wraps the row in {enquiry: {...}} — unwrap so form fields see fields directly.
      setEnq(e?.enquiry || e);
      setDocs(await fetchEnquiryDocs(id));
    } catch (e) {
      toast.error('Failed to load enquiry');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const EXIT_STAGES = ['Lost', 'Parked', 'On Hold'];
  const isExit = EXIT_STAGES.includes(enq?.stage);

  const doReopen = async () => {
    const reason = window.prompt('Reason for reopening (required):');
    if (!reason?.trim()) return;
    try {
      const res = await apiPost(`/enquiries/${id}/reopen`, { reason: reason.trim() });
      toast.success(`Reopened to "${res.stage}"`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reopen failed'); }
  };

  const doMarkLost = async () => {
    const reason = window.prompt('Lost reason (required — price / technical / scope / client dropped / other):');
    if (!reason?.trim()) return;
    try {
      await apiPost(`/enquiries/${id}/mark-lost`, { reason: reason.trim() });
      toast.success('Enquiry marked as Lost');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const doPutOnHold = async () => {
    const reason = window.prompt('On Hold reason (required):');
    if (!reason?.trim()) return;
    const reviewDate = window.prompt('Review date (YYYY-MM-DD, optional — press Enter to skip):') || '';
    try {
      await apiPost(`/enquiries/${id}/put-on-hold`, { reason: reason.trim(), review_date: reviewDate.trim() || null });
      toast.success('Enquiry put on hold');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const upd = (k) => (v) => setEnq((prev) => ({ ...(prev || {}), [k]: v }));

  // Common cleanup applied before every POST/PUT — strips read-only meta keys
  // and coerces blank numerics so backend Pydantic models accept the payload.
  const _scrubPayload = (raw) => {
    const payload = { ...raw };
    ['_id', 'id', 'created_at', 'created_by', 'updated_at', 'updated_by',
     'tender_documents', 'site_visit_report', 'pushed_to_tendering',
     'pushed_to_planning', 'pushed_plan_id', 'pushed_planning_at',
     'pushed_at', 'pushed_enquiry_id', 'pushed_tender_id', 'awarded_at',
     'awarded_project_id', 'is_deleted', 'deleted_at',
     'ai_extracted_at', 'ai_extracted_by',
     'source_opportunity_id', 'source_tender_id',
    ].forEach((k) => delete payload[k]);
    ['estimated_value_cr', 'tender_fee', 'emd_amount',
     'mobilisation_advance_pct', 'retention_pct'].forEach((k) => {
      if (payload[k] === '' || payload[k] == null) payload[k] = null;
      else payload[k] = Number(payload[k]);
    });
    return payload;
  };

  // Iter 137 — atomic + idempotent draft creation.
  //
  // Guarantees:
  //   1. Exactly one POST /enquiries ever runs per component instance.
  //   2. Every concurrent caller (auto-save timer, uploadDocs, runAiExtract,
  //      save, removeDoc, QualificationSWOT) receives the SAME enquiry id.
  //   3. The doc list is re-fetched against the new id so AI extract and the
  //      on-screen list can never target two different enquiries.
  const ensureSaved = async (silent = false) => {
    // Fast-path: a real id is already confirmed for this instance.
    if (savedIdRef.current) return savedIdRef.current;
    // Concurrent-call dedupe: somebody else is mid-POST — await that same promise.
    if (ensureSavedPromiseRef.current) {
      try { return await ensureSavedPromiseRef.current; } catch { return null; }
    }
    // #21/#22 — Client/Project are BD-owned and no longer typed here, so the
    // draft is created unconditionally. Doc upload itself stays gated by
    // `startHereComplete` (BD origin OR client+project) at the uploader.
    // Mark the auto-save timer as "handled" BEFORE the await so the debounce
    // effect cannot queue a duplicate run on a re-render mid-flight.
    autoSaveTriedRef.current = true;

    const promise = (async () => {
      const created = await apiPost('/enquiries', _scrubPayload(enq));
      // Backend wraps the row as `{id, enquiry: {...}}` (same shape as the GET
      // detail endpoint). Unwrap so form field reads (`enq.client`, etc.) hit
      // real values — else setEnq(created) would blank every field.
      const row = created?.enquiry || created;
      // Pin the id immediately so any caller that wakes up on the next tick
      // hits the fast-path instead of trying to POST again.
      savedIdRef.current = row.id || created.id;
      justSavedRef.current = true;
      setEnq(row);
      navigate(`/app/estimation/enquiries/${savedIdRef.current}`, { replace: true });
      // Re-fetch the document list against the confirmed id from the unified
      // store. If a doc was uploaded earlier in this same tick (DocumentUploader
      // awaited ensureSaved), the optimistic onChange already shows it; this
      // GET reconciles with the server.
      setDocs(await fetchEnquiryDocs(savedIdRef.current));
      if (!silent) toast.success('Draft enquiry created');
      return savedIdRef.current;
    })();

    ensureSavedPromiseRef.current = promise;
    try {
      return await promise;
    } catch (e) {
      // Roll back the gates so a retry is possible. savedIdRef stays null
      // because the POST never succeeded.
      ensureSavedPromiseRef.current = null;
      autoSaveTriedRef.current = false;
      if (!silent) toast.error(e?.response?.data?.detail || 'Could not save draft — fix errors and retry');
      return null;
    }
    // On success we intentionally leave ensureSavedPromiseRef pinned: any
    // future call short-circuits via savedIdRef.current === created.id.
  };

  const save = async () => {
    setSaving(true);
    setSaveBlockMessage(null);
    try {
      const hadSavedId = !!savedIdRef.current;
      const targetId = savedIdRef.current || await ensureSaved();
      if (!targetId) return;
      if (hadSavedId) {
        await apiPut(`/enquiries/${targetId}`, _scrubPayload(enq));
        toast.success('Saved');
      }
    } catch (e) {
      const errData = e?.response?.data;
      const code = errData?.code || errData?.detail?.code;
      const msg = errData?.message || errData?.detail?.message || errData?.detail || 'Save failed';
      if (code === 'management_approval_required' || code === 'site_visit_report_required' || code === 'final_decision_required' || code === 'site_visit_choice_required') {
        setSaveBlockMessage(msg);
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  // Iter 136/137 — Silently auto-save a draft once Client + Project Name are
  // both populated in create mode. The user sees the document upload zone go
  // active without an extra click. Debounced 1.2s. Calls the same idempotent
  // ensureSaved() — even if the user clicks "Upload" or "AI Auto-Fill" before
  // this timer fires, only ONE POST will ever run.
  useEffect(() => {
    if (!isCreating) return;
    if (autoSaveTriedRef.current) return;
    if (savedIdRef.current) return;
    if (!enq?.client?.trim() || !enq?.project_name?.trim()) return;
    const timer = setTimeout(() => {
      if (autoSaveTriedRef.current || savedIdRef.current) return;
      ensureSaved(true);
    }, 1200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enq?.client, enq?.project_name, isCreating]);

  const runAiExtract = async () => {
    const targetId = savedIdRef.current || await ensureSaved();
    if (!targetId) return;
    if (docs.length === 0) { toast.error('Upload at least one document first'); return; }
    setExtracting(true);
    try {
      const r = await apiPost(`/enquiries/${targetId}/ai-extract`);
      const applied = r.applied || {};
      const n = Object.keys(applied).length;
      if (Object.keys(applied).length) setAiAppliedKeys((prev) => [...new Set([...prev, ...Object.keys(applied)])]);
      if (n === 0) {
        toast.info(r.message || 'Nothing new extracted');
      } else {
        toast.success(`AI filled ${n} blank field${n === 1 ? '' : 's'}. Existing values preserved.`);
        // Reload the enquiry with the freshly applied AI values
        const e = await apiGet(`/enquiries/${targetId}`);
        setEnq(e?.enquiry || e);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI extract failed');
    } finally {
      setExtracting(false);
    }
  };

  const pushToPlanning = async () => {
    if ((enq?.stage || '') !== 'Won' && (enq?.stage || '') !== 'Awarded') {
      toast.error('Mark enquiry as "Won" before pushing to Project Planning AI');
      return;
    }
    if (enq?.pushed_to_planning) {
      toast.info('Already pushed. Opening the plan…');
      navigate(`/app/project-planning/${enq.pushed_plan_id}`);
      return;
    }
    if (!window.confirm('Push this awarded enquiry to Project Planning AI? A planning card will be created with all uploaded documents attached.')) return;
    setPushing(true);
    try {
      const r = await apiPost(`/enquiries/${id}/push-to-planning`);
      toast.success(`Created planning card with ${r.documents_carried} doc(s)`);
      navigate(`/app/project-planning/${r.plan_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Push failed');
    } finally {
      setPushing(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!enq) return <div className="p-8 text-rose-700">Enquiry not found.</div>;

  const stage = enq.stage || 'Lead Identified';
  const isAwarded = stage === 'Won' || stage === 'Awarded';
  const clientFilled = !!enq?.client?.trim();
  const projectFilled = !!enq?.project_name?.trim();
  const fromBD = !!enq.source_opportunity_id;
  const startHereComplete = fromBD || (clientFilled && projectFilled);
  const draftSaved = !isCreating;
  const aiFilledSet = new Set([...(enq.ai_filled_fields || []), ...aiAppliedKeys]);
  const sectionHasAi = (fields) => fields.some((f) => aiFilledSet.has(f));

  // Stage navigation helpers
  const currentStageSections = STAGE_SECTIONS[stage] || ['basic_info', 'tender_docs'];
  const currentIdx = STAGE_ORDER.indexOf(stage);
  const nextStage = (currentIdx >= 0 && currentIdx < STAGE_ORDER.length - 3) ? STAGE_ORDER[currentIdx + 1] : null;
  const prevStage = currentIdx > 0 ? STAGE_ORDER[currentIdx - 1] : null;

  // GO/NO-GO handlers for Provisional Qualification stage
  const handleManagementApproval = async (decision) => {
    // Tendering_30Sep2026 §3: NO-GO must actually park the enquiry (with a mandatory
    // reason), not just store a flag on /management-approval — route it through
    // /provisional-decision instead, which parks + stops these buttons reappearing
    // (the panel disappears once the stage moves off Provisional Qualification).
    if (decision === 'NO-GO') {
      if (!approvalNote.trim()) { toast.error('A reason is required to record NO-GO.'); return; }
      if (!window.confirm(`Record NO-GO? "${enq.project_name || enq.enquiry_no}" will be PARKED (not archived — re-entry is allowed).`)) return;
      try {
        const r = await apiPost(`/enquiries/${enq.id}/provisional-decision`, { decision: 'NO-GO', note: approvalNote });
        toast.success(r.message || 'Enquiry parked (NO-GO)');
        setApprovalOverride(false);
        setApprovalNote('');
        setSaveBlockMessage(null);
        const fresh = await apiGet(`/enquiries/${enq.id}`);
        setEnq(fresh?.enquiry || fresh);
      } catch (e) { toast.error(e?.response?.data?.detail || 'Decision failed'); }
      return;
    }
    try {
      const r = await apiPost(`/enquiries/${enq.id}/management-approval`, { decision, note: approvalNote });
      toast.success(`Management decision recorded: ${decision}`);
      setApprovalOverride(false);
      setApprovalNote('');
      setSaveBlockMessage(null);
      const fresh = await apiGet(`/enquiries/${enq.id}`);
      setEnq(fresh?.enquiry || fresh);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Only CEO, CMD, MD, or Engineer (M1) can approve.');
      } else {
        toast.error(e?.response?.data?.detail || 'Approval failed');
      }
    }
  };

  const handleSiteVisitDecision = async (siteVisitYes) => {
    if (!siteVisitYes) {
      if (!window.confirm(`Proceed WITHOUT a site visit? "${enq.project_name || enq.enquiry_no}" will move to Costing and Documentation and the site-visit report will be waived. This does NOT archive the enquiry.`)) return;
    }
    try {
      const r = await apiPost(`/enquiries/${enq.id}/provisional-decision`, { decision: 'GO', site_visit: siteVisitYes });
      toast.success(r.message || 'Site visit decision recorded');
      setSaveBlockMessage(null);
      const fresh = await apiGet(`/enquiries/${enq.id}`);
      setEnq(fresh?.enquiry || fresh);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Decision failed'); }
  };

  const handleFinalDecision = async (decision) => {
    if (decision === 'NO-GO') {
      if (!window.confirm(`Record final NO-GO? "${enq.project_name || enq.enquiry_no}" will be PARKED with the SVR attached (re-entry allowed).`)) return;
    }
    try {
      const r = await apiPost(`/enquiries/${enq.id}/final-decision`, { decision, note: finalNote });
      toast.success(r.message || 'Final decision recorded');
      setFinalNote('');
      setSaveBlockMessage(null);
      const fresh = await apiGet(`/enquiries/${enq.id}`);
      setEnq(fresh?.enquiry || fresh);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Decision failed'); }
  };

  return (
    <div className="space-y-4 pb-10">
      <button onClick={() => navigate('/app/estimation/enquiries')} className="text-xs text-slate-500 hover:text-[#3b4872] flex items-center gap-1" data-testid="enq-back">
        <ArrowLeft size={12} /> Back to Enquiries
      </button>

      {/* §6 Parked / §5 Lost / §5 On Hold banner with Reopen action */}
      {!isCreating && isExit && enq && (
        <div className={`rounded-sm border p-4 flex items-start justify-between gap-4 ${enq.stage === 'Lost' ? 'bg-rose-50 border-rose-200' : enq.stage === 'Parked' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`} data-testid="enq-exit-banner">
          <div className="flex-1 text-sm">
            <div className="font-bold text-slate-800 mb-1">
              {enq.stage === 'Parked' && `Parked — NO-GO at ${enq.exited_from_stage || 'Provisional Qualification'}`}
              {enq.stage === 'Lost' && `Lost — ${enq.lost_reason || 'no reason recorded'}`}
              {enq.stage === 'On Hold' && `On Hold — ${enq.on_hold_reason || 'no reason recorded'}`}
            </div>
            <div className="text-xs text-slate-600 space-y-0.5">
              {enq.parked_by && <div>Decided by: <strong>{enq.parked_by}</strong> on {(enq.parked_at || '').slice(0, 10)}</div>}
              {enq.on_hold_by && <div>Put on hold by: <strong>{enq.on_hold_by}</strong> on {(enq.on_hold_at || '').slice(0, 10)}{enq.on_hold_review_date ? ` · review by ${enq.on_hold_review_date}` : ''}</div>}
              {enq.lost_by && <div>Lost by: <strong>{enq.lost_by}</strong> on {(enq.lost_at || '').slice(0, 10)}</div>}
              {enq.park_reason && <div>Reason: {enq.park_reason}</div>}
              {enq.site_visit_report && <div>SVR attached — <a href={`/api/enquiries/${enq.id}/site-visit-report/pdf`} target="_blank" rel="noreferrer" className="underline text-[#3b4872]">Download SVR PDF</a></div>}
            </div>
          </div>
          <button onClick={doReopen} className="px-3 py-1.5 border border-[#3b4872] text-[#3b4872] text-xs rounded-sm font-semibold hover:bg-[#3b4872]/5 flex-shrink-0" data-testid="enq-reopen-btn">
            Reopen
          </button>
        </div>
      )}

      {/* Sticky header + action bar — stays reachable while scrolling the long form */}
      <div className="sticky top-0 z-20 -mx-4 px-4 pt-2 pb-3 bg-slate-50/95 backdrop-blur border-b border-slate-200">
        <PageHeader
          title={isCreating ? 'New Tender Enquiry' : (enq.project_name || 'Untitled Enquiry')}
          subtitle={isCreating
            ? 'Step 1: Upload tender documents · Step 2: AI Auto-Fill the attributes below'
            : `${enq.enquiry_no || '—'}  ·  ${enq.client || '—'}  ·  Stage: ${stage}`}
          actions={
            <div className="flex items-center gap-2">
              <button onClick={save} disabled={saving} data-testid="enq-save-btn" className="px-4 py-2 bg-[#3b4872] text-white text-xs font-display font-bold uppercase tracking-wide rounded-md hover:bg-[#2c3658] disabled:opacity-50">
                {saving ? 'Saving…' : (isCreating ? 'Create Enquiry' : 'Save')}
              </button>
              <button
                onClick={runAiExtract}
                disabled={extracting || docs.length === 0}
                data-testid="enq-ai-extract-btn"
                className="px-4 py-2 bg-violet-600 text-white text-xs font-display font-bold uppercase tracking-wide rounded-md hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1.5"
                title={docs.length === 0 ? 'Upload at least one document first' : 'Read docs and fill blank fields (never overwrites)'}
              >
                {extracting ? <><Loader2 size={12} className="animate-spin" /> Extracting…</> : <><Sparkles size={12} /> AI Auto-Fill</>}
              </button>
              {!isCreating && (
                <button
                  onClick={pushToPlanning}
                  disabled={pushing || !isAwarded}
                  data-testid="enq-push-planning-btn"
                  className="px-4 py-2 bg-emerald-600 text-white text-xs font-display font-bold uppercase tracking-wide rounded-md hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5"
                  title={isAwarded ? 'Hand off to Project Planning AI' : 'Available once stage = Won'}
                >
                  {pushing ? <><Loader2 size={12} className="animate-spin" /> Pushing…</> : <><Send size={12} /> {enq.pushed_to_planning ? 'Open Plan' : 'Push to Planning AI'}</>}
                </button>
              )}
            </div>
          }
        />
      </div>

      {/* Iter 195 — tabs for existing enquiries: Details / Deadlines / Revisions / Activity */}
      {!isCreating && (
        <div className="flex items-center gap-1 border-b border-slate-200" data-testid="enq-tabs">
          {[['details', 'Details'], ['deadlines', 'Deadlines'], ['revisions', 'Revisions'], ['meetings', 'Meetings'], ['activity', 'Activity Log']].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`enq-tab-${k}`}
              className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-wider -mb-px border-b-2 ${tab === k ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {!isCreating && tab === 'deadlines' && (
        <EnquiryDeadlinesPanel enqId={enq.id} enq={enq} onUpdated={load} />
      )}
      {!isCreating && tab === 'revisions' && (
        <EnquiryRevisionsTab enqId={enq.id} docs={docs} enqStage={enq.stage} onUpdated={load} />
      )}
      {!isCreating && tab === 'activity' && (
        <EnquiryActivityTab enqId={enq.id} />
      )}
      {!isCreating && tab === 'meetings' && (
        <MeetingsTab enqId={enq.id} />
      )}

      {/* DETAILS tab — default; always shown in create mode */}
      <div className={(isCreating || tab === 'details') ? 'space-y-4' : 'hidden'} data-testid="enq-tab-panel-details">

      {/* Iter 136 — three-step banner with checkmarks, only in create mode */}
      {isCreating && (
        <div className="bg-violet-50 border border-violet-200 rounded-md p-4 text-xs text-violet-900" data-testid="enq-create-steps">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-violet-600" />
            <strong className="font-display uppercase tracking-wide text-[12px]">How this works — 3 steps</strong>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <li className={`flex items-start gap-2 rounded-md p-3 border ${startHereComplete ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-violet-200'}`} data-testid="enq-step-1">
              <span className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold ${startHereComplete ? 'bg-emerald-600 text-white' : 'bg-violet-600 text-white'}`}>
                {startHereComplete ? '✓' : '1'}
              </span>
              <div>
                <div className="font-display font-bold text-[12px] uppercase tracking-wide">Client &amp; Project come from BD</div>
                <div className="text-[11px] mt-0.5 text-violet-700">Identity is set automatically when the enquiry is pushed from Business Development. Document upload unlocks once that identity exists.</div>
              </div>
            </li>
            <li className={`flex items-start gap-2 rounded-md p-3 border ${docs.length > 0 ? 'bg-emerald-50 border-emerald-200' : (startHereComplete ? 'bg-white border-violet-200' : 'bg-white border-violet-100 opacity-60')}`} data-testid="enq-step-2">
              <span className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold ${docs.length > 0 ? 'bg-emerald-600 text-white' : 'bg-violet-600 text-white'}`}>
                {docs.length > 0 ? '✓' : '2'}
              </span>
              <div>
                <div className="font-display font-bold text-[12px] uppercase tracking-wide">Upload tender documents</div>
                <div className="text-[11px] mt-0.5 text-violet-700">BOQ, RFP, drawings, GSS / SCC — PDF / Excel / DXF / image.</div>
              </div>
            </li>
            <li className={`flex items-start gap-2 rounded-md p-3 border ${docs.length > 0 ? 'bg-white border-violet-200' : 'bg-white border-violet-100 opacity-60'}`} data-testid="enq-step-3">
              <span className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold bg-violet-600 text-white">3</span>
              <div>
                <div className="font-display font-bold text-[12px] uppercase tracking-wide">Click "AI Auto-Fill"</div>
                <div className="text-[11px] mt-0.5 text-violet-700">Ellora AI reads your docs and fills the 30+ fields below. Review &amp; tweak.</div>
              </div>
            </li>
          </ol>
          {draftSaved && (
            <p className="text-[11px] text-emerald-700 mt-3 font-display font-bold uppercase tracking-wide flex items-center gap-1.5" data-testid="enq-draft-saved-inline">
              <FileCheck2 size={12} /> Draft saved — you can keep working, all edits are preserved.
            </p>
          )}
        </div>
      )}

      {/* ─── Documents (Project-Planning-AI styled dropzone) ── */}
      <Section
        title="Tender Documents"
        hint={isCreating
          ? "Upload BOQ / tender doc / GSS / SCC / drawings. A draft enquiry is saved automatically once Client + Project Name are set."
          : "Upload BOQ, tender doc, GSS, SCC, drawings, etc. AI Auto-Fill reads these to populate blank fields below — it never overwrites existing values."}
        icon={<FileText size={16} />}
        testid="enq-section-documents"
      >
        {/* Format guidance — same amber callout as Planning AI */}
        <div className="bg-amber-50 border-l-4 border-amber-500 px-3 py-2.5 rounded-sm text-xs text-amber-900 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
          <div>
            <strong>For best AI extraction:</strong> upload <u>BOQ as Excel/PDF</u>, <u>Drawings as searchable PDF or DXF</u>,
            <u> Tender / SCC / GCC / GSS as PDF</u>. Scanned/image PDFs are OCR'd automatically — quality may vary.
          </div>
        </div>

        {/* Inline gate notice — visible (NOT a tooltip) so touch users see it */}
        {isCreating && !startHereComplete && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-md px-3 py-2 flex items-start gap-2" data-testid="enq-upload-gate-msg">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span><strong>Client</strong> &amp; <strong>Project</strong> arrive when this enquiry is pushed from Business Development. Document upload unlocks once that identity is present.</span>
          </div>
        )}

        {/* Category selector + dashed dropzone — shared <DocumentUploader>.
            Iter 165 — onChange triggers a refetch from the unified store
            so the rendered list always matches db.documents (the legacy
            embedded tender_documents[] is no longer the source of truth). */}
        <div className="mt-3">
          <DocumentUploader
            entityType="enquiry"
            entityId={savedIdRef.current}
            documents={docs}
            onChange={async () => {
              if (savedIdRef.current) setDocs(await fetchEnquiryDocs(savedIdRef.current));
            }}
            categories={ENQUIRY_DOC_CATEGORIES}
            defaultCategory="BOQ"
            resolveEntityId={ensureSaved}
            disabled={isCreating && !startHereComplete}
            testidPrefix="enq"
          />
        </div>

        {/* AI Auto-Fill inline helper — visible text, not a tooltip */}
        {docs.length === 0 && startHereComplete && (
          <div className="bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-md px-3 py-2 flex items-start gap-2" data-testid="enq-ai-gate-msg">
            <Sparkles size={13} className="mt-0.5 shrink-0 text-violet-600" />
            <span>Upload at least one document above to enable <strong>AI Auto-Fill</strong>.</span>
          </div>
        )}

        {enq.ai_extracted_at && (
          <p className="text-[10px] text-violet-700 italic mt-2">
            ✨ AI last filled fields on {_fmtDT(enq.ai_extracted_at)} by {enq.ai_extracted_by}
          </p>
        )}
      </Section>

      {/* ── Management Approval (GO/NO-GO) ─────────────────────────────── */}
      {enq.stage === 'Provisional Qualification' && (
        <div className="border border-blue-200 rounded-md p-4 bg-blue-50 mb-4" data-testid="provisional-qualification-panel">
          <div className="text-sm font-display font-bold text-blue-800 mb-1">Management Approval Required</div>
          <p className="text-xs text-blue-700 mb-3">CEO / CMD / MD / Engineer (M1) must approve before this enquiry can proceed to Site Visit.</p>

          {/* Already decided — show read-only badge */}
          {/* §1 Tendering_452 fix: show badge only when BOTH management_approval AND site_visit_decision are set.
              When management_approval='GO' but site_visit_decision is not yet set, show the site-visit Yes/No panel. */}
          {enq.management_approval && enq.site_visit_decision && !approvalOverride ? (
            <div>
              {enq.management_approval === 'GO' ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-sm text-sm font-bold" data-testid="enq-approval-go-badge">
                    ✓ GO — Approved by {enq.management_approval_by} on {enq.management_approval_at?.slice(0, 10)}
                  </span>
                  {enq.management_approval_note && <span className="text-xs text-emerald-700 italic">"{enq.management_approval_note}"</span>}
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-100 text-rose-800 border border-rose-200 rounded-sm text-sm font-bold" data-testid="enq-approval-nogo-badge">
                    ✗ NO-GO — by {enq.management_approval_by} on {enq.management_approval_at?.slice(0, 10)}
                  </span>
                  {enq.management_approval_note && <span className="text-xs text-rose-700 italic">"{enq.management_approval_note}"</span>}
                </div>
              )}
              <button onClick={() => setApprovalOverride(true)} className="mt-2 text-[10px] text-slate-500 hover:text-[#3b4872] underline" data-testid="enq-approval-change">Change decision</button>
            </div>
          ) : (
            /* §1 Tendering_452: after GO is recorded, show site-visit Yes/No before the footer nav */
            <div className="space-y-3">
              {/* If management_approval is not yet set, show GO / NO-GO */}
              {enq.management_approval !== 'GO' ? (
                <>
                  <div className="flex gap-3">
                    <button onClick={() => handleManagementApproval('GO')}
                      className="px-5 py-2 bg-emerald-600 text-white text-sm font-bold rounded-sm hover:bg-emerald-700"
                      data-testid="enq-go-btn">✓ GO</button>
                    <button onClick={() => handleManagementApproval('NO-GO')}
                      disabled={!approvalNote.trim()}
                      title={!approvalNote.trim() ? 'A reason is required for NO-GO' : undefined}
                      className="px-5 py-2 bg-rose-600 text-white text-sm font-bold rounded-sm hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="enq-nogo-btn">✗ NO-GO</button>
                  </div>
                  <input
                    value={approvalNote}
                    onChange={e => setApprovalNote(e.target.value)}
                    placeholder="Optional for GO — required reason for NO-GO"
                    className="w-full border border-blue-200 rounded-sm px-3 py-1.5 text-xs bg-white focus:outline-none focus:border-[#3b4872]"
                    data-testid="enq-approval-note"
                  />
                </>
              ) : !enq.site_visit_decision ? (
                /* GO recorded but site visit decision not yet made */
                <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-4 space-y-2" data-testid="site-visit-decision-panel">
                  <div className="text-sm font-bold text-emerald-800">GO recorded — Site visit needed?</div>
                  <p className="text-xs text-emerald-700">Select Yes to go through the site-visit report. Select No to skip the SVR and go straight to Costing.</p>
                  <div className="flex gap-3">
                    <button onClick={() => handleSiteVisitDecision(true)}
                      className="px-5 py-2 bg-emerald-600 text-white text-sm font-bold rounded-sm hover:bg-emerald-700"
                      data-testid="enq-svr-yes-btn">Yes — Site Visit required</button>
                    <button onClick={() => handleSiteVisitDecision(false)}
                      className="px-4 py-2 bg-slate-600 text-white text-sm font-bold rounded-sm hover:bg-slate-700"
                      data-testid="enq-svr-no-btn">No — Skip to Costing</button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-emerald-700 font-semibold" data-testid="enq-go-recorded">
                  ✓ GO — {enq.site_visit_decision === 'YES' ? 'moving to Site Visit' : 'SVR waived, moving to Costing'}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Final GO/NO-GO after site-visit report (Batch C §2) ────────── */}
      {enq.stage === 'Site Visit' && enq.site_visit_report && (
        <div className="border border-indigo-200 rounded-md p-4 bg-indigo-50 mb-4" data-testid="final-decision-panel">
          <div className="text-sm font-display font-bold text-indigo-800 mb-1">Final Decision (after site-visit report)</div>
          <p className="text-xs text-indigo-700 mb-3">CEO / CMD / MD / Super Admin / Engineer (M1) must record the final GO before this enquiry can move to Costing. NO-GO archives it.</p>
          {enq.final_decision ? (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm font-bold border ${
              enq.final_decision === 'GO' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-rose-100 text-rose-800 border-rose-200'
            }`} data-testid="enq-final-decision-badge">
              {enq.final_decision === 'GO' ? '✓ Final GO' : '✗ Final NO-GO'} — by {enq.final_decision_by} on {enq.final_decision_at?.slice(0, 10)}
              {enq.final_decision_note ? ` · "${enq.final_decision_note}"` : ''}
            </span>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-3">
                <button onClick={() => handleFinalDecision('GO')}
                  className="px-5 py-2 bg-emerald-600 text-white text-sm font-bold rounded-sm hover:bg-emerald-700"
                  data-testid="enq-final-go-btn">✓ Final GO</button>
                <button onClick={() => handleFinalDecision('NO-GO')}
                  className="px-5 py-2 bg-rose-600 text-white text-sm font-bold rounded-sm hover:bg-rose-700"
                  data-testid="enq-final-nogo-btn">✗ Final NO-GO</button>
              </div>
              <input
                value={finalNote}
                onChange={e => setFinalNote(e.target.value)}
                placeholder="Optional note (required-ish for NO-GO reason)"
                className="w-full border border-indigo-200 rounded-sm px-3 py-1.5 text-xs bg-white focus:outline-none focus:border-[#3b4872]"
                data-testid="enq-final-note"
              />
            </div>
          )}
        </div>
      )}

      {/* Batch C §7 — Vendors & rates on the Costing stage */}
      {enq.stage === 'Costing and Documentation' && <CostingVendorsPanel enqId={enq.id} />}

      {/* Inline save-block message */}
      {saveBlockMessage && (
        <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 text-xs text-amber-900 mb-2 flex items-start gap-2" data-testid="enq-save-block-msg">
          <AlertTriangle size={14} className="shrink-0 mt-0.5 text-amber-600" />
          <span>{saveBlockMessage}</span>
        </div>
      )}

      {/* ─── Basic Info ──────────────────────────────────────────────── */}
      {currentStageSections.includes('basic_info') && (
      <Section
        title="Basic Information"
        hint="Tender identification, scope and submission window. Client, Project &amp; Tender Title are set by BD and shown in the header."
        icon={<Building2 size={16} />}
        collapsible
        defaultOpen
        count={15}
        testid="enq-section-basic"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="Tender Number" value={enq.tender_number} onChange={upd('tender_number')} placeholder="TND-2026-001" testid="enq-tender-number" />
          <Field label="Estimated Value (₹ Cr)" value={enq.estimated_value_cr} onChange={upd('estimated_value_cr')} type="number" testid="enq-value" />
          <Field label="Submission Deadline" value={enq.submission_deadline} onChange={upd('submission_deadline')} type="date" testid="enq-deadline" />
          <div>
            <label className={lbl}>Category</label>
            <div className="mt-1.5">
              <CategoryMultiCombobox
                value={enq.categories && enq.categories.length ? enq.categories : (enq.category ? [enq.category] : [])}
                onChange={(arr) => setEnq((prev) => ({ ...(prev || {}), categories: arr, category: arr[0] || '' }))}
                testid="enq-category"
              />
            </div>
          </div>
          <div>
            <label className={lbl}>Nature of Project</label>
            <div className="mt-1">
              <NatureCombobox value={enq.nature_of_project} onChange={upd('nature_of_project')} testid="enq-nature" />
            </div>
          </div>
          {fromBD ? (
            <div>
              <label className={lbl}>Sector</label>
              <input
                value={enq.sector ?? ''}
                readOnly
                disabled
                className={`${inp} mt-1 bg-slate-100 text-slate-600 cursor-not-allowed`}
                data-testid="enq-sector"
              />
              <p className="text-[10px] text-slate-400 mt-1" data-testid="enq-sector-hint">Auto-filled from BD opportunity</p>
            </div>
          ) : (
            <Select label="Sector" value={enq.sector} onChange={upd('sector')} options={['Pharma', 'Auto', 'Industrial', 'Govt', 'Infra', 'IT/ITES', 'Logistics', 'Other']} testid="enq-sector" />
          )}
          <Select label="Mode of Tender" value={enq.mode_of_tender} onChange={upd('mode_of_tender')} options={['Open', 'Limited', 'Single Source', 'E-Tender', 'Walk-in']} testid="enq-mode" />
          {/* Batch C follow-up: Walk-in tender identity */}
          <div className="sm:col-span-2 lg:col-span-3">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600" data-testid="enq-walkin-toggle">
              <input type="checkbox" checked={!!enq.walk_in_tender}
                onChange={e => setEnq(prev => ({ ...(prev || {}), walk_in_tender: e.target.checked }))}
                className="rounded" data-testid="enq-walkin-checkbox" />
              Walk-in tender (client approached Ellora directly — not via portal)
            </label>
          </div>
          {enq.walk_in_tender && (
            <>
              <Field label="Client's Reference No." value={enq.client_tender_ref}
                onChange={upd('client_tender_ref')} placeholder="Client's NIT / RFQ number"
                testid="enq-client-tender-ref" />
              <Field label="Received Date" value={enq.walk_in_received_date}
                onChange={upd('walk_in_received_date')} type="date"
                testid="enq-walkin-received-date" />
              <Field label="Received By (Ellora)" value={enq.walk_in_received_by}
                onChange={upd('walk_in_received_by')} placeholder="Name of Ellora person who received"
                testid="enq-walkin-received-by" />
            </>
          )}
          <div>
            <label className={lbl}>Location</label>
            <div className="mt-1">
              <LocationCombobox value={enq.location} onChange={upd('location')} testid="enq-location" />
            </div>
          </div>
          <Field label="Estimated Start Date" value={enq.estimated_start_date} onChange={upd('estimated_start_date')} type="date" testid="enq-start-date" />
          <Field label="Estimated End Date" value={enq.estimated_end_date} onChange={upd('estimated_end_date')} type="date" testid="enq-end-date" />
          <Select label="Priority" value={enq.priority} onChange={upd('priority')} options={['Low', 'Medium', 'High', 'Critical']} testid="enq-priority" />
          {/* Tendering_30Sep2026 §9: payment terms live in Basic Info (visible from
              Lead Identified through Won) instead of only in the collapsed
              Financial Terms section between Costing and Negotiation. */}
          <Field label="Payment terms (as per tender)" value={enq.payment_terms} onChange={upd('payment_terms')} placeholder="e.g. 10% advance, 5% retention, 45 days credit" testid="enq-payment-terms" />
          <Field label="Advance (%)" value={enq.payment_advance_pct} onChange={upd('payment_advance_pct')} type="number" testid="enq-payment-advance" />
          <Field label="Retention on payment (%)" value={enq.payment_retention_pct} onChange={upd('payment_retention_pct')} type="number" testid="enq-payment-retention" />
          <Field label="Credit days" value={enq.payment_credit_days} onChange={upd('payment_credit_days')} type="number" testid="enq-payment-credit-days" />
        </div>
      </Section>
      )}

      {/* ─── Financial Terms ─────────────────────────────────────────── */}
      {currentStageSections.includes('financial_terms') && (
      <Section
        title="Financial Terms"
        hint="Tender fee, EMD, advance / retention, billing cycle."
        icon={<IndianRupee size={16} />}
        collapsible
        defaultOpen={false}
        count={8}
        badge={sectionHasAi(FINANCIAL_FIELDS) ? <AiFilledBadge section="financial" /> : null}
        testid="enq-section-financial"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="Tender Fee (₹)" value={enq.tender_fee} onChange={upd('tender_fee')} type="number" testid="enq-tender-fee" />
          <Select label="EMD Type" value={enq.emd_type} onChange={upd('emd_type')} options={['FDR', 'BG', 'Online', 'Exempt']} testid="enq-emd-type" />
          <Field label="EMD Amount (₹)" value={enq.emd_amount} onChange={upd('emd_amount')} type="number" testid="enq-emd-amount" />
          <Field label="Defect Liability Period" value={enq.defect_liability_period} onChange={upd('defect_liability_period')} placeholder="e.g. 12 months" testid="enq-dlp" />
          <Field label="Mobilisation Advance (%)" value={enq.mobilisation_advance_pct} onChange={upd('mobilisation_advance_pct')} type="number" testid="enq-mob-adv" />
          <Field label="Retention (%)" value={enq.retention_pct} onChange={upd('retention_pct')} type="number" testid="enq-retention" />
          <Select label="RA Billing Cycle" value={enq.ra_billing_cycle} onChange={upd('ra_billing_cycle')} options={['Monthly', 'Fortnightly', 'Milestone']} testid="enq-ra-cycle" />
          <Select label="Client Payment Risk" value={enq.client_payment_risk} onChange={upd('client_payment_risk')} options={['Low', 'Medium', 'High']} testid="enq-pay-risk" />
        </div>
      </Section>
      )}

      {/* ─── Contract Clauses ────────────────────────────────────────── */}
      {currentStageSections.includes('contract_clauses') && (
      <Section
        title="Contract Clauses"
        hint="Material rates, escalation, LD and bonus clauses."
        icon={<FileCheck2 size={16} />}
        collapsible
        defaultOpen={false}
        count={4}
        badge={sectionHasAi(CONTRACT_FIELDS) ? <AiFilledBadge section="contract" /> : null}
        testid="enq-section-contract"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Textarea label="Basic Material Rate" value={enq.basic_material_rate} onChange={upd('basic_material_rate')} testid="enq-basic-material" />
          <Textarea label="Price Escalation Clause" value={enq.price_escalation_clause} onChange={upd('price_escalation_clause')} testid="enq-escalation" />
          <Textarea label="LD Clause" value={enq.ld_clause} onChange={upd('ld_clause')} testid="enq-ld" />
          <Textarea label="Bonus Clause" value={enq.bonus_clause} onChange={upd('bonus_clause')} testid="enq-bonus" />
        </div>
      </Section>
      )}

      {/* ─── Qualification — SWOT, Risk, Decision Matrix ─────────────── */}
      {currentStageSections.includes('swot') && (
      <Section
        title={enq.stage === 'Provisional Qualification' ? 'Provisional Qualification — SWOT Analysis' : 'Qualification — SWOT, Risk & Decision'}
        hint="Capture SWOT, risk parameters and Go/No-Go decision matrix per the EEPC qualification template. Surfaces the verdict on the enquiry list."
        icon={<ShieldAlert size={16} />}
        collapsible
        defaultOpen={false}
        testid="enq-section-qualification"
      >
        <QualificationSWOT
          qualification={enq.qualification}
          onChange={(val) => upd('qualification')(val)}
          enquiryId={savedIdRef.current || (isCreating ? null : enq.id)}
          ensureSaved={ensureSaved}
        />
      </Section>
      )}

      {/* ─── Stage History ────────────────────────────────────────────── */}
      {(enq.stage_history || []).length > 0 && (
        <Section title="Stage History" hint="Completed stages and their saved data" icon={<FileText size={16} />} collapsible defaultOpen={false} testid="enq-section-history">
          <div className="space-y-2">
            {(enq.stage_history || []).map((h, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${STAGE_COLORS[h.stage] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{h.stage}</span>
                  <span className="text-[10px] text-slate-400">{h.moved_by || 'System'} · {(h.moved_at || '').slice(0, 10)}</span>
                </div>
                {h.notes && <p className="text-xs text-slate-600 mt-1">{h.notes}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Bottom save bar with stage navigation — NO navigation buttons on exit stages */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 -mx-4 px-4 py-3 flex items-center justify-between z-10">
        <div>
          {/* §3 Tendering_452: hide ← prev-stage button when in exit stage (Lost/Parked/On Hold) */}
          {prevStage && !isCreating && !isExit && (
            <button
              onClick={async () => {
                if (!window.confirm(`Go back to "${prevStage}"?`)) return;
                setSaving(true);
                setSaveBlockMessage(null);
                try {
                  await apiPut(`/enquiries/${savedIdRef.current}`, _scrubPayload({ ...enq, stage: prevStage }));
                  setEnq(prev => ({ ...prev, stage: prevStage }));
                  toast.success(`Moved back to "${prevStage}"`);
                } catch (e) {
                  const errData = e?.response?.data;
                  const msg = errData?.message || errData?.detail?.message || errData?.detail || 'Save failed';
                  toast.error(msg);
                } finally { setSaving(false); }
              }}
              className="flex items-center gap-1.5 px-4 py-2 border border-slate-300 rounded-sm text-sm font-display font-bold text-slate-600 hover:bg-slate-50"
              data-testid="enq-prev-stage-btn">
              ← {prevStage}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={save} disabled={saving} data-testid="enq-save-bottom"
            className="px-5 py-2 border border-[#3b4872] text-[#3b4872] rounded-sm text-sm font-display font-bold hover:bg-indigo-50 disabled:opacity-50">
            {saving ? 'Saving…' : (isCreating ? 'Create Enquiry' : 'Save')}
          </button>
          {/* §5: Mark Lost and Put On Hold at any non-exit stage */}
          {!isCreating && !isExit && !['Won', 'Awarded'].includes(enq.stage) && (
            <>
              <button onClick={doMarkLost} className="px-3 py-2 text-xs border border-rose-300 text-rose-600 rounded-sm font-semibold hover:bg-rose-50" data-testid="enq-mark-lost-btn">Mark Lost</button>
              <button onClick={doPutOnHold} className="px-3 py-2 text-xs border border-slate-300 text-slate-600 rounded-sm font-semibold hover:bg-slate-50" data-testid="enq-on-hold-btn">On Hold</button>
            </>
          )}
          {nextStage && !isCreating && !['Won', 'Lost', 'On Hold', 'Parked', 'Awarded', 'Provisional Qualification'].includes(enq.stage) && (
            <button
              onClick={async () => {
                if (!savedIdRef.current) return;
                setSaving(true);
                setSaveBlockMessage(null);
                try {
                  await apiPut(`/enquiries/${savedIdRef.current}`, _scrubPayload({ ...enq, stage: nextStage }));
                  setEnq(prev => ({ ...prev, stage: nextStage }));
                  toast.success(`Moved to "${nextStage}"`);
                } catch (e) {
                  const errData = e?.response?.data;
                  const code = errData?.code || errData?.detail?.code;
                  const msg = errData?.message || errData?.detail?.message || errData?.detail || 'Save failed';
                  if (code === 'management_approval_required' || code === 'site_visit_report_required' || code === 'final_decision_required' || code === 'site_visit_choice_required') {
                    setSaveBlockMessage(msg);
                  }
                  toast.error(msg);
                } finally { setSaving(false); }
              }}
              disabled={saving}
              className="px-5 py-2 bg-[#3b4872] text-white rounded-sm text-sm font-display font-bold hover:bg-[#2d3856] disabled:opacity-50"
              data-testid="enq-next-stage-btn">
              Save &amp; Move to {nextStage} →
            </button>
          )}
        </div>
      </div>

      </div>
    </div>
  );
}
